import React, { useState, useRef, useCallback } from 'react';
import html2canvas from 'html2canvas';
import JSZip from 'jszip';
import {
  Sparkles, Loader2, Download, ChevronLeft, ChevronRight,
  AlertCircle, RotateCcw, BookOpen, Quote, Megaphone, Zap,
  Monitor, Square, Smartphone, User, Building2,
} from 'lucide-react';
import { useSermons } from '../../hooks/useSermons';
import { supabase } from '../../lib/supabase';
import { logger } from '../../lib/logger';

// ── Types ──────────────────────────────────────────────────────────────────────

type DesignStyle = 'editorial' | 'annotated' | 'modern';
type AccentColor = 'crimson' | 'gold' | 'navy' | 'forest' | 'slate';
type SlideType = 'hook' | 'point' | 'scripture' | 'cta';
type Platform = 'square' | 'portrait' | 'story' | 'wide';

interface Slide {
  type: SlideType;
  headline: string;
  body: string | null;
  slideNumber: number;
  highlightWords?: string[];
}

interface RenderedSlide extends Slide {
  imageUrl: string | null;
  rendering: boolean;
  error: string | null;
  dirty?: boolean;
}

interface Branding {
  pastorName: string;
  churchName: string;
  seriesName: string;
}

// ── Constants ──────────────────────────────────────────────────────────────────

const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];

const ACCENT_COLORS: Record<AccentColor, { hex: string; label: string }> = {
  crimson: { hex: '#8B2020', label: 'Crimson' },
  gold:    { hex: '#A8821A', label: 'Gold'    },
  navy:    { hex: '#1A3254', label: 'Navy'    },
  forest:  { hex: '#2A4A2A', label: 'Forest'  },
  slate:   { hex: '#3A4A5A', label: 'Slate'   },
};

const DESIGN_STYLES: { id: DesignStyle; label: string; description: string }[] = [
  { id: 'editorial', label: 'Editorial',  description: 'Magazine layout — cream background, rule borders, display serif' },
  { id: 'annotated', label: 'Annotated',  description: 'Typewriter text with hand-drawn circles and red script commentary' },
  { id: 'modern',    label: 'Modern Bold', description: 'Pure white, extreme type scale, single bold accent color' },
];

const PLATFORMS: { id: Platform; label: string; size: string; width: number; height: number; icon: React.ReactNode }[] = [
  { id: 'square',   label: 'Square',   size: '1:1',  width: 1080, height: 1080, icon: <Square className="w-3.5 h-3.5" /> },
  { id: 'portrait', label: 'Portrait', size: '4:5',  width: 1080, height: 1350, icon: <Monitor className="w-3.5 h-3.5" /> },
  { id: 'story',    label: 'Story',    size: '9:16', width: 1080, height: 1920, icon: <Smartphone className="w-3.5 h-3.5" /> },
  { id: 'wide',     label: 'Wide',     size: '16:9', width: 1200, height: 630,  icon: <Monitor className="w-3.5 h-3.5" /> },
];

const SLIDE_BADGE: Record<SlideType, { label: string; icon: React.ReactNode; cls: string }> = {
  hook:      { label: 'Hook',      icon: <Zap className="w-3 h-3" />,       cls: 'bg-electric/10 text-electric border-electric/20' },
  point:     { label: 'Point',     icon: <BookOpen className="w-3 h-3" />,  cls: 'bg-emerald/10 text-emerald border-emerald/20' },
  scripture: { label: 'Scripture', icon: <Quote className="w-3 h-3" />,     cls: 'bg-gold/10 text-gold border-gold/20' },
  cta:       { label: 'CTA',       icon: <Megaphone className="w-3 h-3" />, cls: 'bg-crimson/10 text-crimson border-crimson/20' },
};

const PREVIEW_W = 260;

// ── EDITORIAL TEMPLATES ────────────────────────────────────────────────────────

interface EditorialProps {
  slide: Slide;
  accent: string;
  width: number;
  height: number;
  total: number;
  branding: Branding;
}

function EditorialSlide({ slide, accent, width, height, total, branding }: EditorialProps) {
  const pad = Math.round(width * 0.072);
  const borderInset = Math.round(width * 0.028);
  const headerFs = Math.round(width * 0.016);
  const labelFs = Math.round(width * 0.019);
  const bodyFs = Math.round(width * 0.030);
  const refFs = Math.round(width * 0.022);

  const pastorLabel = branding.pastorName.toUpperCase() || 'SERMONBUILD';
  const slideLabel = slide.type === 'hook' ? 'OPENING' : slide.type === 'cta' ? 'CLOSING' : slide.type === 'scripture' ? 'SCRIPTURE' : `POINT ${slide.slideNumber - 1}`;
  const romanNum = ROMAN[slide.slideNumber - 1] ?? String(slide.slideNumber);

  const headFs =
    slide.type === 'scripture'
      ? (slide.headline.length > 90 ? Math.round(width * 0.040) : slide.headline.length > 60 ? Math.round(width * 0.046) : Math.round(width * 0.052))
      : (slide.headline.length > 65 ? Math.round(width * 0.054) : slide.headline.length > 45 ? Math.round(width * 0.062) : Math.round(width * 0.072));

  const base: React.CSSProperties = {
    position: 'absolute', fontFamily: '"Playfair Display", Georgia, serif',
  };

  if (slide.type === 'scripture') {
    return (
      <div style={{ width, height, background: '#F2EBE0', position: 'relative', overflow: 'hidden' }}>
        {/* Outer border */}
        <div style={{ position: 'absolute', inset: borderInset, border: `1px solid #1A1A1A`, pointerEvents: 'none' }} />
        {/* Header */}
        <div style={{ position: 'absolute', top: borderInset + Math.round(height * 0.028), left: pad, right: pad, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: headerFs, fontFamily: 'Inter, sans-serif', fontWeight: 600, letterSpacing: '3px', color: '#1A1A1A' }}>{pastorLabel}</span>
          <span style={{ fontSize: headerFs, fontFamily: 'Inter, sans-serif', fontWeight: 400, letterSpacing: '2px', color: '#555' }}>SCRIPTURE · {romanNum}</span>
        </div>
        {/* Header rule */}
        <div style={{ position: 'absolute', top: borderInset + Math.round(height * 0.075), left: pad, right: pad, height: 1, background: '#1A1A1A' }} />
        {/* Centered content */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: `0 ${pad}px`, textAlign: 'center' }}>
          <div style={{ width: 32, height: 2, background: accent, marginBottom: Math.round(height * 0.04) }} />
          <div style={{ fontSize: headFs, fontStyle: 'italic', color: '#1A1A1A', lineHeight: 1.48, maxWidth: '88%', ...base, position: 'relative' }}>
            "{slide.headline}"
          </div>
          {slide.body && (
            <div style={{ marginTop: Math.round(height * 0.042), fontSize: refFs, fontFamily: 'Inter, sans-serif', fontWeight: 600, letterSpacing: '3px', color: accent }}>
              — {slide.body.toUpperCase()}
            </div>
          )}
        </div>
        {/* Footer */}
        <div style={{ position: 'absolute', bottom: borderInset + Math.round(height * 0.025), left: pad, right: pad, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: headerFs, fontFamily: 'Inter, sans-serif', letterSpacing: '2px', color: '#888' }}>{String(slide.slideNumber).padStart(2, '0')} / {String(total).padStart(2, '0')}</span>
          {branding.churchName && <span style={{ fontSize: headerFs, fontFamily: 'Inter, sans-serif', letterSpacing: '1px', color: '#888' }}>{branding.churchName.toUpperCase()}</span>}
        </div>
      </div>
    );
  }

  return (
    <div style={{ width, height, background: '#F2EBE0', position: 'relative', overflow: 'hidden' }}>
      {/* Outer border */}
      <div style={{ position: 'absolute', inset: borderInset, border: `1px solid #1A1A1A`, pointerEvents: 'none' }} />
      {/* Header bar */}
      <div style={{ position: 'absolute', top: borderInset + Math.round(height * 0.028), left: pad, right: pad, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: headerFs, fontFamily: 'Inter, sans-serif', fontWeight: 600, letterSpacing: '3px', color: '#1A1A1A' }}>{pastorLabel}</span>
        <span style={{ fontSize: headerFs, fontFamily: 'Inter, sans-serif', fontWeight: 400, letterSpacing: '2px', color: '#555' }}>{slideLabel} · {romanNum}</span>
      </div>
      {/* Header rule */}
      <div style={{ position: 'absolute', top: borderInset + Math.round(height * 0.075), left: pad, right: pad, height: 1, background: '#1A1A1A' }} />

      {/* Roman numeral + label block */}
      <div style={{ position: 'absolute', top: borderInset + Math.round(height * 0.115), left: pad, display: 'flex', alignItems: 'baseline', gap: Math.round(width * 0.016) }}>
        <span style={{ fontSize: Math.round(width * 0.048), fontWeight: 700, color: accent, fontFamily: '"Playfair Display", Georgia, serif', lineHeight: 1 }}>{romanNum}.</span>
        <span style={{ fontSize: labelFs, fontFamily: 'Inter, sans-serif', fontWeight: 400, letterSpacing: '4px', color: '#555' }}>{branding.seriesName ? branding.seriesName.toUpperCase() : slideLabel}</span>
      </div>
      {/* Thin rule below numeral */}
      <div style={{ position: 'absolute', top: borderInset + Math.round(height * 0.178), left: pad, right: pad, height: 1, background: '#CCC' }} />

      {/* Headline */}
      <div style={{
        position: 'absolute',
        top: borderInset + Math.round(height * 0.205),
        left: pad, right: pad,
        fontSize: headFs,
        fontWeight: 700,
        color: '#1A1A1A',
        lineHeight: 1.18,
        fontFamily: '"Playfair Display", Georgia, serif',
      }}>
        {slide.headline}
      </div>

      {/* Body — for point slides */}
      {slide.body && (
        <>
          <div style={{ position: 'absolute', top: borderInset + Math.round(height * 0.205) + headFs * 1.18 * Math.ceil(slide.headline.length / 28) + Math.round(height * 0.032), left: pad, width: 40, height: 1, background: accent, opacity: 0.6 }} />
          <div style={{
            position: 'absolute',
            top: borderInset + Math.round(height * 0.205) + headFs * 1.18 * Math.ceil(slide.headline.length / 28) + Math.round(height * 0.062),
            left: pad, right: pad,
            fontSize: bodyFs,
            color: '#333',
            lineHeight: 1.7,
            fontFamily: '"Playfair Display", Georgia, serif',
            fontStyle: 'normal',
            fontWeight: 400,
          }}>
            {slide.body}
          </div>
        </>
      )}

      {/* Scripture reference (for hook/cta) */}
      {!slide.body && slide.type !== 'hook' && (
        <div style={{
          position: 'absolute',
          bottom: borderInset + Math.round(height * 0.092),
          right: pad,
          fontSize: refFs,
          fontFamily: 'Inter, sans-serif',
          letterSpacing: '2px',
          color: '#888',
        }}>
          {branding.seriesName ? branding.seriesName.toUpperCase() : ''}
        </div>
      )}

      {/* Footer */}
      <div style={{ position: 'absolute', bottom: borderInset + Math.round(height * 0.025), left: pad, right: pad }}>
        <div style={{ height: 1, background: '#CCC', marginBottom: Math.round(height * 0.018) }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: headerFs, fontFamily: 'Inter, sans-serif', letterSpacing: '2px', color: '#888' }}>{String(slide.slideNumber).padStart(2, '0')} / {String(total).padStart(2, '0')}</span>
          {branding.churchName && <span style={{ fontSize: headerFs, fontFamily: 'Inter, sans-serif', letterSpacing: '1px', color: '#888' }}>{branding.churchName.toUpperCase()}</span>}
        </div>
      </div>
    </div>
  );
}

// ── ANNOTATED TEMPLATES ────────────────────────────────────────────────────────

interface AnnotatedProps {
  slide: Slide;
  accent: string;
  width: number;
  height: number;
  total: number;
  branding: Branding;
}

function AnnotatedSlide({ slide, accent, width, height, total, branding }: AnnotatedProps) {
  const pad = Math.round(width * 0.082);
  const headerFs = Math.round(width * 0.015);
  const mainFs = slide.headline.length > 60 ? Math.round(width * 0.038) : slide.headline.length > 40 ? Math.round(width * 0.044) : Math.round(width * 0.050);
  const bodyFs = Math.round(width * 0.032);
  const refFs = Math.round(width * 0.028);

  const highlights = (slide.highlightWords ?? []).map(w => w.toUpperCase());

  // Split headline into segments, marking highlight words
  const renderAnnotatedHeadline = () => {
    if (!highlights.length) {
      return (
        <span style={{ fontFamily: '"Courier New", Courier, monospace', fontWeight: 700, fontSize: mainFs, color: '#1A1A1A', letterSpacing: '2px', textTransform: 'uppercase', lineHeight: 1.55 }}>
          {slide.headline.toUpperCase()}
        </span>
      );
    }

    const upperHeadline = slide.headline.toUpperCase();
    const parts: { text: string; highlight: boolean; circle?: boolean }[] = [];
    let remaining = upperHeadline;

    // Simple greedy split by highlight words
    highlights.forEach((hw, idx) => {
      const pos = remaining.indexOf(hw);
      if (pos === -1) return;
      if (pos > 0) parts.push({ text: remaining.slice(0, pos), highlight: false });
      parts.push({ text: hw, highlight: true, circle: idx === 0 });
      remaining = remaining.slice(pos + hw.length);
    });
    if (remaining) parts.push({ text: remaining, highlight: false });

    return (
      <span style={{ fontFamily: '"Courier New", Courier, monospace', fontSize: mainFs, color: '#1A1A1A', letterSpacing: '2px', textTransform: 'uppercase', lineHeight: 1.55 }}>
        {parts.map((part, i) =>
          part.highlight ? (
            <span key={i} style={{
              fontWeight: 900,
              textDecoration: part.circle ? 'none' : 'underline',
              textUnderlineOffset: '4px',
              display: 'inline-block',
              ...(part.circle ? {
                border: `2.5px solid #1A1A1A`,
                borderRadius: '50% / 40%',
                padding: `${Math.round(mainFs * 0.08)}px ${Math.round(mainFs * 0.22)}px`,
                margin: `0 ${Math.round(mainFs * 0.05)}px`,
                lineHeight: 1.3,
              } : {}),
            }}>
              {part.text}
            </span>
          ) : (
            <span key={i} style={{ fontWeight: 400 }}>{part.text}</span>
          )
        )}
      </span>
    );
  };

  const showArrow = slide.body && slide.type === 'point';

  return (
    <div style={{ width, height, background: '#F5F4F2', position: 'relative', overflow: 'hidden' }}>
      {/* Subtle paper texture via gradient */}
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 30% 20%, rgba(0,0,0,0.018) 0%, transparent 60%), radial-gradient(ellipse at 80% 80%, rgba(0,0,0,0.015) 0%, transparent 50%)', pointerEvents: 'none' }} />

      {/* Centered content block */}
      <div style={{ position: 'absolute', top: 0, left: pad, right: pad, bottom: Math.round(height * 0.10), display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', gap: Math.round(height * 0.028) }}>
        {/* Headline with highlight words */}
        <div style={{ maxWidth: '88%' }}>
          {renderAnnotatedHeadline()}
        </div>

        {/* Arrow SVG (point slides only) */}
        {showArrow && (
          <svg width={Math.round(width * 0.055)} height={Math.round(width * 0.07)} viewBox="0 0 40 55" fill="none" style={{ opacity: 0.7 }}>
            <path d="M20 4 L20 44 M10 34 L20 47 L30 34" stroke="#1A1A1A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}

        {/* Body — red italic script style */}
        {slide.body && (
          <div style={{
            fontSize: bodyFs,
            color: accent,
            fontStyle: 'italic',
            fontFamily: '"Courier New", Courier, monospace',
            lineHeight: 1.72,
            maxWidth: '78%',
            textAlign: 'center',
            letterSpacing: '0.5px',
          }}>
            {slide.body}
          </div>
        )}

        {/* Scripture reference */}
        {slide.type === 'scripture' && slide.body && (
          <div style={{ fontSize: refFs, fontFamily: '"Courier New", Courier, monospace', color: accent, letterSpacing: '1px', marginTop: Math.round(height * 0.01) }}>
            — {slide.body}
          </div>
        )}
      </div>

      {/* Bottom bar */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: Math.round(height * 0.082), background: '#1A1A1A', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: `0 ${pad}px` }}>
        <span style={{ fontSize: headerFs, fontFamily: 'Inter, sans-serif', fontWeight: 700, letterSpacing: '3px', color: '#FFF' }}>
          {branding.seriesName ? branding.seriesName.toUpperCase() : (branding.churchName ? branding.churchName.toUpperCase() : 'SERMONBUILD')}
        </span>
        <span style={{ fontSize: headerFs, fontFamily: 'Inter, sans-serif', letterSpacing: '2px', color: 'rgba(255,255,255,0.5)' }}>
          {String(slide.slideNumber).padStart(2, '0')} / {String(total).padStart(2, '0')}
        </span>
      </div>
    </div>
  );
}

// ── MODERN BOLD TEMPLATES ──────────────────────────────────────────────────────

interface ModernProps {
  slide: Slide;
  accent: string;
  width: number;
  height: number;
  total: number;
  branding: Branding;
}

function ModernSlide({ slide, accent, width, height, total, branding }: ModernProps) {
  const pad = Math.round(width * 0.072);
  const headerFs = Math.round(width * 0.017);
  const labelFs = Math.round(width * 0.016);
  const bodyFs = Math.round(width * 0.033);

  const headFs =
    slide.headline.length > 65 ? Math.round(width * 0.062) :
    slide.headline.length > 45 ? Math.round(width * 0.072) :
    Math.round(width * 0.084);

  // Split headline: last word gets accent color
  const words = slide.headline.split(' ');
  const mainWords = words.slice(0, -1).join(' ');
  const lastWord = words[words.length - 1];

  const typeLabel = slide.type === 'hook' ? 'HOOK' : slide.type === 'cta' ? 'CLOSE' : slide.type === 'scripture' ? 'SCRIPTURE' : `POINT ${slide.slideNumber - 1}`;

  return (
    <div style={{ width, height, background: '#FFFFFF', position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      {/* Left accent strip */}
      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: Math.round(width * 0.012), background: accent }} />

      {/* Header */}
      <div style={{ paddingLeft: pad + Math.round(width * 0.012), paddingRight: pad, paddingTop: Math.round(height * 0.052), display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: headerFs, fontFamily: 'Inter, sans-serif', fontWeight: 800, letterSpacing: '4px', color: '#111' }}>
          {branding.pastorName.toUpperCase() || 'SERMONBUILD'}
        </span>
        <span style={{ fontSize: labelFs, fontFamily: 'Inter, sans-serif', fontWeight: 400, letterSpacing: '3px', color: '#AAA' }}>{typeLabel}</span>
      </div>

      {/* Main content — vertically centered */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', paddingLeft: pad + Math.round(width * 0.012), paddingRight: pad, paddingBottom: Math.round(height * 0.06) }}>
        {slide.type === 'scripture' ? (
          <>
            <div style={{ width: 48, height: 4, background: accent, marginBottom: Math.round(height * 0.038) }} />
            <div style={{ fontSize: Math.round(headFs * 0.72), fontStyle: 'italic', color: '#111', lineHeight: 1.45, fontFamily: '"Playfair Display", Georgia, serif', fontWeight: 700, maxWidth: '90%' }}>
              "{slide.headline}"
            </div>
            {slide.body && (
              <div style={{ marginTop: Math.round(height * 0.038), fontSize: bodyFs, fontFamily: 'Inter, sans-serif', fontWeight: 700, letterSpacing: '3px', color: accent }}>
                {slide.body.toUpperCase()}
              </div>
            )}
          </>
        ) : (
          <>
            <div style={{ fontSize: headFs, fontWeight: 900, color: '#111', lineHeight: 1.1, fontFamily: 'Inter, sans-serif', maxWidth: '92%', marginBottom: slide.body ? Math.round(height * 0.04) : 0 }}>
              {mainWords && <span>{mainWords} </span>}
              <span style={{ color: accent }}>{lastWord}</span>
            </div>
            {slide.body && (
              <>
                <div style={{ width: 40, height: 3, background: '#EEE', marginBottom: Math.round(height * 0.032) }} />
                <div style={{ fontSize: bodyFs, color: '#444', lineHeight: 1.68, fontFamily: 'Inter, sans-serif', fontWeight: 400, maxWidth: '82%' }}>
                  {slide.body}
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Footer bar */}
      <div style={{ paddingLeft: pad + Math.round(width * 0.012), paddingRight: pad, paddingBottom: Math.round(height * 0.042), display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {branding.churchName
          ? <span style={{ fontSize: labelFs, fontFamily: 'Inter, sans-serif', fontWeight: 600, letterSpacing: '2px', color: '#AAA' }}>{branding.churchName.toUpperCase()}</span>
          : <span />
        }
        <span style={{ fontSize: labelFs, fontFamily: 'Inter, sans-serif', fontWeight: 400, letterSpacing: '2px', color: '#CCC' }}>{slide.slideNumber} / {total}</span>
      </div>
    </div>
  );
}

// ── Slide dispatcher ───────────────────────────────────────────────────────────

interface SlideTemplateProps {
  slide: Slide;
  designStyle: DesignStyle;
  accent: string;
  width: number;
  height: number;
  total: number;
  branding: Branding;
}

function SlideTemplate({ slide, designStyle, accent, width, height, total, branding }: SlideTemplateProps) {
  const props = { slide, accent, width, height, total, branding };
  if (designStyle === 'annotated') return <AnnotatedSlide {...props} />;
  if (designStyle === 'modern')    return <ModernSlide    {...props} />;
  return <EditorialSlide {...props} />;
}

// ── Main component ─────────────────────────────────────────────────────────────

export function SocialCarouselBuilder({ defaultSermonId }: { defaultSermonId?: string } = {}) {
  const { sermons } = useSermons();

  const [sermonId, setSermonId]       = useState(defaultSermonId ?? '');
  const [designStyle, setDesignStyle] = useState<DesignStyle>('editorial');
  const [accent, setAccent]           = useState<AccentColor>('crimson');
  const [platform, setPlatform]       = useState<Platform>('square');
  const [slideCount, setSlideCount]   = useState(5);
  const [aiModel, setAiModel]         = useState<'haiku' | 'sonnet'>('sonnet');
  const [branding, setBranding]       = useState<Branding>({ pastorName: '', churchName: '', seriesName: '' });

  const [generating, setGenerating]   = useState(false);
  const [genError, setGenError]       = useState<string | null>(null);
  const [slides, setSlides]           = useState<RenderedSlide[]>([]);
  const [activeIdx, setActiveIdx]     = useState(0);
  const [downloadingAll, setDownloadingAll] = useState(false);

  const captureRef  = useRef<HTMLDivElement>(null);
  const [captureData, setCaptureData] = useState<{ slide: Slide; width: number; height: number } | null>(null);

  const sermon     = sermons.find(s => s.id === sermonId);
  const platformCfg = PLATFORMS.find(p => p.id === platform)!;
  const accentHex  = ACCENT_COLORS[accent].hex;
  const prevH      = Math.round(PREVIEW_W * (platformCfg.height / platformCfg.width));

  const googleFontsUrl = designStyle === 'editorial'
    ? 'https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;1,700&family=Inter:wght@400;600&display=swap'
    : designStyle === 'modern'
      ? 'https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@1,700&family=Inter:wght@400;600;800;900&display=swap'
      : 'https://fonts.googleapis.com/css2?family=Inter:wght@400;700&display=swap'; // annotated uses system Courier

  const toPlainText = (html: string) => {
    const div = document.createElement('div');
    div.innerHTML = html;
    return (div.textContent || div.innerText || '').trim();
  };

  const captureToBlob = async (): Promise<Blob> => {
    if (!captureRef.current) throw new Error('Capture element not ready');
    await document.fonts.ready;
    await new Promise(r => setTimeout(r, 300));
    const canvas = await html2canvas(captureRef.current, {
      width: platformCfg.width,
      height: platformCfg.height,
      scale: 1,
      useCORS: true,
      allowTaint: false,
      logging: false,
      onclone: (doc) => {
        const style = doc.createElement('style');
        style.textContent = `@import url('${googleFontsUrl}');`;
        doc.head.appendChild(style);
      },
    });
    return new Promise((resolve, reject) => {
      canvas.toBlob(b => b ? resolve(b) : reject(new Error('Canvas export failed')), 'image/png');
    });
  };

  const renderSlides = useCallback(async (rawSlides: Slide[], plt: Platform) => {
    const cfg = PLATFORMS.find(p => p.id === plt)!;
    for (let i = 0; i < rawSlides.length; i++) {
      const s = rawSlides[i];
      setCaptureData({ slide: s, width: cfg.width, height: cfg.height });
      await new Promise(r => setTimeout(r, 380));
      try {
        const blob = await captureToBlob();
        const url = URL.createObjectURL(blob);
        setSlides(prev => prev.map((ps, pi) => pi === i ? { ...ps, imageUrl: url, rendering: false } : ps));
      } catch (err) {
        logger.error('Slide capture error:', err);
        setSlides(prev => prev.map((ps, pi) => pi === i ? { ...ps, rendering: false, error: 'Render failed' } : ps));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designStyle, accentHex, branding, platformCfg]);

  const generate = async () => {
    if (!sermon) return;
    setGenerating(true);
    setGenError(null);
    setSlides([]);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Please sign in');

      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-social-slides`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            title: sermon.title,
            scripture: sermon.scripture,
            content: toPlainText(sermon.content || '').slice(0, 4000),
            slideCount,
            style: designStyle,
            seriesName: branding.seriesName,
            model: aiModel,
          }),
        }
      );

      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed to generate slides');

      const rawSlides: Slide[] = data.slides;
      const initial: RenderedSlide[] = rawSlides.map(s => ({ ...s, imageUrl: null, rendering: true, error: null }));
      setSlides(initial);
      setActiveIdx(0);

      await renderSlides(rawSlides, platform);
    } catch (err) {
      logger.error('generate error:', err);
      setGenError(err instanceof Error ? err.message : 'Failed to generate slides');
    } finally {
      setGenerating(false);
    }
  };

  const updateSlideField = (idx: number, field: 'headline' | 'body', value: string) => {
    setSlides(prev => prev.map((ps, i) => i === idx ? { ...ps, [field]: value, dirty: true } : ps));
  };

  const reRender = async (idx: number) => {
    const s = slides[idx];
    if (!s) return;
    setSlides(prev => prev.map((ps, i) => i === idx ? { ...ps, rendering: true, error: null } : ps));
    setCaptureData({ slide: s, width: platformCfg.width, height: platformCfg.height });
    await new Promise(r => setTimeout(r, 380));
    try {
      const blob = await captureToBlob();
      const url = URL.createObjectURL(blob);
      setSlides(prev => prev.map((ps, i) => i === idx ? { ...ps, imageUrl: url, rendering: false, dirty: false } : ps));
    } catch {
      setSlides(prev => prev.map((ps, i) => i === idx ? { ...ps, rendering: false, error: 'Render failed' } : ps));
    }
  };

  const downloadSlide = (slide: RenderedSlide, idx: number) => {
    if (!slide.imageUrl) return;
    const a = document.createElement('a');
    a.href = slide.imageUrl;
    a.download = `slide-${String(idx + 1).padStart(2, '0')}-${slide.type}.png`;
    a.click();
  };

  const downloadAll = async () => {
    const ready = slides.filter(s => s.imageUrl);
    if (!ready.length) return;
    setDownloadingAll(true);
    try {
      const zip = new JSZip();
      for (let i = 0; i < slides.length; i++) {
        const s = slides[i];
        if (!s.imageUrl) continue;
        const blob = await fetch(s.imageUrl).then(r => r.blob());
        zip.file(`slide-${String(i + 1).padStart(2, '0')}-${s.type}.png`, blob);
      }
      const blob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${sermon?.title || 'carousel'}-${designStyle}-${platform}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloadingAll(false);
    }
  };

  const hasSlides = slides.length > 0;
  const allRendered = hasSlides && slides.every(s => !s.rendering);
  const activeSlide = slides[activeIdx];
  const isCapturing = hasSlides && slides.some(s => s.rendering);

  return (
    <div className="space-y-5">

      {/* Off-screen capture target */}
      <div aria-hidden="true" style={{ position: 'fixed', left: '-99999px', top: 0, zIndex: -1 }}>
        <div ref={captureRef} style={{ width: captureData?.width ?? platformCfg.width, height: captureData?.height ?? platformCfg.height, overflow: 'hidden' }}>
          {captureData && (
            <SlideTemplate
              slide={captureData.slide}
              designStyle={designStyle}
              accent={accentHex}
              width={captureData.width}
              height={captureData.height}
              total={slides.length || slideCount}
              branding={branding}
            />
          )}
        </div>
      </div>

      {/* ── STEP 1: SETUP ──────────────────────────────────────────────── */}
      <div className="rounded-2xl border border-sand/20 dark:border-slate/20 bg-white dark:bg-charcoal overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-sand/20 dark:border-slate/20 bg-sand/20 dark:bg-slate/10">
          <span className="w-6 h-6 rounded-full bg-electric text-white text-xs font-bold flex items-center justify-center">1</span>
          <div>
            <h2 className="text-sm font-bold text-midnight dark:text-soft-white">Setup</h2>
            <p className="text-xs text-slate/50 dark:text-soft-white/40">Sermon, branding, and format</p>
          </div>
        </div>

        <div className="p-5 space-y-6">

          {/* Sermon select */}
          <div>
            <label className="block text-xs font-semibold text-slate dark:text-soft-white/50 uppercase tracking-wider mb-1.5">Sermon</label>
            <select
              value={sermonId}
              onChange={e => setSermonId(e.target.value)}
              className="w-full rounded-lg border border-sand/30 dark:border-slate/30 bg-white dark:bg-charcoal text-midnight dark:text-soft-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-electric/30"
            >
              <option value="">Select a sermon…</option>
              {sermons.map(s => <option key={s.id} value={s.id}>{s.title}</option>)}
            </select>
          </div>

          {/* Branding fields */}
          <div>
            <label className="block text-xs font-semibold text-slate dark:text-soft-white/50 uppercase tracking-wider mb-2">Branding</label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate/40" />
                <input
                  type="text"
                  placeholder="Pastor Name"
                  value={branding.pastorName}
                  onChange={e => setBranding(b => ({ ...b, pastorName: e.target.value }))}
                  className="w-full pl-8 pr-3 py-2 rounded-lg border border-sand/30 dark:border-slate/30 bg-white dark:bg-charcoal text-midnight dark:text-soft-white text-sm focus:outline-none focus:ring-2 focus:ring-electric/30"
                />
              </div>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate/40" />
                <input
                  type="text"
                  placeholder="Church / Website"
                  value={branding.churchName}
                  onChange={e => setBranding(b => ({ ...b, churchName: e.target.value }))}
                  className="w-full pl-8 pr-3 py-2 rounded-lg border border-sand/30 dark:border-slate/30 bg-white dark:bg-charcoal text-midnight dark:text-soft-white text-sm focus:outline-none focus:ring-2 focus:ring-electric/30"
                />
              </div>
              <input
                type="text"
                placeholder="Series Name (optional)"
                value={branding.seriesName}
                onChange={e => setBranding(b => ({ ...b, seriesName: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-sand/30 dark:border-slate/30 bg-white dark:bg-charcoal text-midnight dark:text-soft-white text-sm focus:outline-none focus:ring-2 focus:ring-electric/30"
              />
            </div>
          </div>

          {/* Design style + accent color */}
          <div>
            <label className="block text-xs font-semibold text-slate dark:text-soft-white/50 uppercase tracking-wider mb-2">Design Style</label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
              {DESIGN_STYLES.map(ds => (
                <button
                  key={ds.id}
                  onClick={() => setDesignStyle(ds.id)}
                  className={`text-left rounded-xl border-2 px-3.5 py-3 transition-all ${designStyle === ds.id ? 'border-electric bg-electric/5 dark:bg-electric/10' : 'border-sand/20 dark:border-slate/30 hover:border-electric/40'}`}
                >
                  <div className="text-sm font-semibold text-midnight dark:text-soft-white mb-0.5">{ds.label}</div>
                  <div className="text-xs text-slate/50 dark:text-soft-white/40 leading-snug">{ds.description}</div>
                </button>
              ))}
            </div>

            <label className="block text-xs font-semibold text-slate dark:text-soft-white/50 uppercase tracking-wider mb-2">Accent Color</label>
            <div className="flex gap-2 flex-wrap">
              {(Object.entries(ACCENT_COLORS) as [AccentColor, { hex: string; label: string }][]).map(([id, cfg]) => (
                <button
                  key={id}
                  onClick={() => setAccent(id)}
                  title={cfg.label}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border-2 text-xs font-medium transition-all ${accent === id ? 'border-electric' : 'border-sand/20 dark:border-slate/30 hover:border-electric/40'}`}
                >
                  <span className="w-3.5 h-3.5 rounded-full" style={{ background: cfg.hex }} />
                  <span className="text-slate dark:text-soft-white/70">{cfg.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Format + slide count */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs font-semibold text-slate dark:text-soft-white/50 uppercase tracking-wider mb-2">Format</label>
              <div className="flex flex-col gap-1.5">
                {PLATFORMS.map(p => (
                  <button key={p.id} onClick={() => setPlatform(p.id)}
                    className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border text-sm font-medium transition-all ${platform === p.id ? 'bg-electric text-white border-electric shadow-sm' : 'bg-white dark:bg-charcoal text-slate dark:text-soft-white/60 border-sand/30 dark:border-slate/30 hover:border-electric/50 hover:text-electric'}`}
                  >
                    {p.icon}
                    <span>{p.label}</span>
                    <span className={`text-xs ml-auto ${platform === p.id ? 'text-white/60' : 'text-slate/40'}`}>{p.size}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate dark:text-soft-white/50 uppercase tracking-wider mb-2">Slides</label>
              <div className="flex gap-2">
                {[5, 6, 7].map(n => (
                  <button key={n} onClick={() => setSlideCount(n)}
                    className={`w-12 h-12 rounded-xl text-sm font-bold border-2 transition-all ${slideCount === n ? 'bg-electric text-white border-electric' : 'bg-white dark:bg-charcoal text-slate dark:text-soft-white/60 border-sand/30 dark:border-slate/30 hover:border-electric/50'}`}
                  >
                    {n}
                  </button>
                ))}
              </div>

              <label className="block text-xs font-semibold text-slate dark:text-soft-white/50 uppercase tracking-wider mt-4 mb-2">AI Model</label>
              <div className="flex gap-2">
                {([['sonnet', 'Sonnet'], ['haiku', 'Haiku']] as const).map(([id, label]) => (
                  <button key={id} onClick={() => setAiModel(id)}
                    className={`px-4 h-10 rounded-xl text-sm font-semibold border-2 transition-all ${aiModel === id ? 'bg-electric text-white border-electric' : 'bg-white dark:bg-charcoal text-slate dark:text-soft-white/60 border-sand/30 dark:border-slate/30 hover:border-electric/50'}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-slate/40 dark:text-soft-white/30 mt-1.5">Sonnet writes more cohesive, natural copy (recommended). Haiku is faster and cheaper.</p>
            </div>
          </div>

          {genError && (
            <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-crimson/10 text-crimson text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />{genError}
            </div>
          )}

          <button onClick={generate} disabled={generating || isCapturing || !sermonId}
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 bg-electric text-white rounded-xl font-semibold hover:bg-electric/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm shadow-electric/20 text-sm"
          >
            {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> Reading sermon…</>
              : isCapturing ? <><Loader2 className="w-4 h-4 animate-spin" /> Rendering slides…</>
              : <><Sparkles className="w-4 h-4" /> Generate Carousel</>}
          </button>
          {(generating || isCapturing) && (
            <p className="text-xs text-slate/50 dark:text-soft-white/30">
              {generating ? 'Claude is reading your sermon and writing the slides…' : `Rendering slide ${slides.findIndex(s => s.rendering) + 1} of ${slides.length}…`}
            </p>
          )}
        </div>
      </div>

      {/* ── STEP 2: PREVIEW ────────────────────────────────────────────── */}
      {hasSlides && (
        <div className="rounded-2xl border border-sand/20 dark:border-slate/20 bg-white dark:bg-charcoal overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-sand/20 dark:border-slate/20 bg-sand/20 dark:bg-slate/10">
            <div className="flex items-center gap-3">
              <span className="w-6 h-6 rounded-full bg-electric text-white text-xs font-bold flex items-center justify-center">2</span>
              <div>
                <h2 className="text-sm font-bold text-midnight dark:text-soft-white">Preview & Download</h2>
                <p className="text-xs text-slate/50 dark:text-soft-white/40">{allRendered ? `${slides.length} slides ready` : 'Rendering…'}</p>
              </div>
            </div>
            {allRendered && (
              <button onClick={downloadAll} disabled={downloadingAll}
                className="flex items-center gap-2 px-4 py-2 bg-emerald text-white rounded-xl text-sm font-semibold hover:bg-emerald/90 disabled:opacity-60 transition-all"
              >
                {downloadingAll ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                Download ZIP
              </button>
            )}
          </div>

          <div className="p-5">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6">

              {/* Slide content cards */}
              <div className="space-y-2">
                {slides.map((s, i) => {
                  const badge = SLIDE_BADGE[s.type];
                  return (
                    <div key={i} onClick={() => setActiveIdx(i)}
                      className={`rounded-xl border-2 cursor-pointer transition-all overflow-hidden ${activeIdx === i ? 'border-electric shadow-sm shadow-electric/10' : 'border-sand/20 dark:border-slate/20 hover:border-electric/40'}`}
                    >
                      <div className={`flex items-center gap-2 px-3 py-2 ${activeIdx === i ? 'bg-electric/5 dark:bg-electric/10' : 'bg-sand/10 dark:bg-slate/10'}`}>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wide ${badge.cls}`}>
                          {badge.icon}{badge.label}
                        </span>
                        <span className="text-xs text-slate/40 dark:text-soft-white/30 ml-auto">Slide {s.slideNumber}</span>
                        {s.rendering && <span className="text-[10px] text-electric font-medium">rendering…</span>}
                        {s.dirty && !s.rendering && <span className="text-[10px] text-gold font-medium">edited</span>}
                        {s.error && <span className="text-[10px] text-crimson font-medium">failed</span>}
                      </div>
                      {activeIdx === i ? (
                        <div className="px-3 py-2.5 space-y-2" onClick={e => e.stopPropagation()}>
                          <div>
                            <label className="block text-[10px] font-semibold text-slate/40 dark:text-soft-white/30 uppercase tracking-wider mb-1">
                              {s.type === 'scripture' ? 'Verse' : 'Headline'}
                            </label>
                            <textarea
                              value={s.headline}
                              onChange={e => updateSlideField(i, 'headline', e.target.value)}
                              rows={2}
                              className="w-full text-sm font-semibold text-midnight dark:text-soft-white leading-snug bg-sand/10 dark:bg-slate/20 border border-sand/30 dark:border-slate/30 rounded-lg px-2.5 py-2 resize-none focus:outline-none focus:border-electric"
                            />
                          </div>
                          {(s.type === 'point' || s.type === 'scripture') && (
                            <div>
                              <label className="block text-[10px] font-semibold text-slate/40 dark:text-soft-white/30 uppercase tracking-wider mb-1">
                                {s.type === 'scripture' ? 'Reference' : 'Body'}
                              </label>
                              <textarea
                                value={s.body ?? ''}
                                onChange={e => updateSlideField(i, 'body', e.target.value)}
                                rows={s.type === 'scripture' ? 1 : 3}
                                className="w-full text-xs text-slate/70 dark:text-soft-white/60 leading-relaxed bg-sand/10 dark:bg-slate/20 border border-sand/30 dark:border-slate/30 rounded-lg px-2.5 py-2 resize-none focus:outline-none focus:border-electric"
                              />
                            </div>
                          )}
                          {s.dirty && (
                            <button onClick={() => reRender(i)} disabled={s.rendering}
                              className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 bg-electric text-white rounded-lg text-xs font-semibold hover:bg-electric/90 disabled:opacity-60 transition-all"
                            >
                              {s.rendering ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                              Apply changes
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="px-3 py-2.5 space-y-1">
                          <p className="text-sm font-semibold text-midnight dark:text-soft-white leading-snug">{s.headline}</p>
                          {s.body && <p className="text-xs text-slate/60 dark:text-soft-white/50 leading-relaxed">{s.body}</p>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Preview + actions */}
              <div className="flex flex-col items-center gap-4">
                <div
                  className="rounded-xl overflow-hidden border border-sand/20 dark:border-slate/20 shadow-lg flex-shrink-0 flex items-center justify-center"
                  style={{ width: PREVIEW_W, height: prevH, background: '#F2EBE0' }}
                >
                  {activeSlide?.rendering ? (
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="w-5 h-5 animate-spin text-electric" />
                      <span className="text-xs text-slate/40">Rendering…</span>
                    </div>
                  ) : activeSlide?.error ? (
                    <div className="flex flex-col items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-crimson" />
                      <button onClick={() => reRender(activeIdx)} className="text-xs text-electric hover:underline">Retry</button>
                    </div>
                  ) : activeSlide?.imageUrl ? (
                    <img src={activeSlide.imageUrl} alt={`Slide ${activeSlide.slideNumber}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <span className="text-xs text-slate/30">No preview</span>
                  )}
                </div>

                {slides.length > 1 && (
                  <div className="flex items-center gap-2">
                    <button onClick={() => setActiveIdx(i => Math.max(0, i - 1))} disabled={activeIdx === 0}
                      className="p-1.5 rounded-lg border border-sand/30 dark:border-slate/30 text-slate hover:border-electric/50 hover:text-electric disabled:opacity-30 transition-all"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="text-sm text-slate/50 dark:text-soft-white/40 min-w-[52px] text-center">{activeIdx + 1} / {slides.length}</span>
                    <button onClick={() => setActiveIdx(i => Math.min(slides.length - 1, i + 1))} disabled={activeIdx === slides.length - 1}
                      className="p-1.5 rounded-lg border border-sand/30 dark:border-slate/30 text-slate hover:border-electric/50 hover:text-electric disabled:opacity-30 transition-all"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                )}

                <div className="flex flex-col gap-2 w-full">
                  {activeSlide?.imageUrl && (
                    <button onClick={() => downloadSlide(activeSlide, activeIdx)}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-electric text-white rounded-xl font-semibold hover:bg-electric/90 transition-all text-sm shadow-sm shadow-electric/20"
                    >
                      <Download className="w-4 h-4" /> Download Slide {activeIdx + 1}
                    </button>
                  )}
                  {activeSlide?.error && (
                    <button onClick={() => reRender(activeIdx)}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-sand/30 dark:border-slate/30 text-slate dark:text-soft-white/60 rounded-xl text-sm hover:border-electric/50 hover:text-electric transition-all"
                    >
                      <RotateCcw className="w-4 h-4" /> Retry Render
                    </button>
                  )}
                </div>

                {slides.length > 1 && (
                  <div className="w-full">
                    <p className="text-xs font-semibold text-slate/40 dark:text-soft-white/30 uppercase tracking-wider mb-2">All Slides</p>
                    <div className="flex gap-2 overflow-x-auto pb-1">
                      {slides.map((s, i) => {
                        const thumbH = Math.round(52 * (platformCfg.height / platformCfg.width));
                        return (
                          <button key={i} onClick={() => setActiveIdx(i)}
                            className={`flex-shrink-0 rounded-md overflow-hidden border-2 transition-all ${activeIdx === i ? 'border-electric' : 'border-transparent hover:border-electric/40'}`}
                            style={{ width: 52, height: thumbH }}
                          >
                            {s.imageUrl
                              ? <img src={s.imageUrl} alt={`Slide ${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              : <div className="w-full h-full bg-sand/20 dark:bg-slate/20 flex items-center justify-center">
                                  {s.rendering && <Loader2 className="w-3 h-3 animate-spin text-electric" />}
                                </div>
                            }
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
